import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  FigureElement,
  FigureHandle,
  ReadoutHandle,
  ReadoutRow,
  GridHandle,
} from '@learnforge/demo-render';
import type { DissectionHandle } from '@learnforge/demo-render/geometry';
import type { GeometryContext } from '../types';
import type { GeometryKey } from '../locales/keys';
import type { Vec2, PiecePlan, Triangle } from '@learnforge/demo-math/geometry';
import {
  dist,
  parallelogramToRect,
  triangleToParallelogram,
  trapezoidToParallelogram,
  circleSectors,
  apothemFan,
  heron,
  shoelaceArea,
  regularPolygonVertices,
  compositeDecompose,
} from '@learnforge/demo-math/geometry';
import { AREA_PRESETS } from '../data/area-presets';
import type { AreaShape } from '../data/area-presets';

// ============================================================================
// Pure helper types
// ============================================================================

export interface ShearModelInput {
  shape: 'parallelogram' | 'triangle' | 'trapezoid';
  base: number;
  height: number;
  shear: number;
  topBase?: number; // trapezoid only
}

export interface ShearModel {
  vertices: Vec2[];
  area: number;
  formula: string;
}

export interface DerivePlanInput {
  shape: AreaShape;
  // parallelogram
  base?: number;
  height?: number;
  shear?: number;
  // triangle
  triangle?: Triangle;
  // trapezoid
  topBase?: number;
  // circle
  radius?: number;
  sectors?: number;
}

export interface RegularPolygonModel {
  vertices: Vec2[];
  perimeter: number;
  apothem: number;
  sideLength: number;
  area: number;
  n: number;
}

export interface CompositePartModel {
  vertices: Vec2[];
  area: number;
}

export interface CompositeModel {
  figureVertices: Vec2[];
  parts: CompositePartModel[];
  totalArea: number;
  cuts: [Vec2, Vec2][];
}

export interface HeronModel {
  heronArea: number;
  shoelaceArea: number;
  sideA: number;
  sideB: number;
  sideC: number;
  semiPerimeter: number;
}

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

/**
 * Build the vertices of a shape with a given shear offset.
 * Returns the polygon vertices and computed area.
 * Area = base × height (parallelogram), ½ × base × height (triangle),
 * ½ × (a+b) × height (trapezoid) — all invariant under shear.
 */
export function buildShearModel(input: ShearModelInput): ShearModel {
  const { shape, base, height, shear } = input;

  let vertices: Vec2[];
  let formula: string;

  if (shape === 'parallelogram') {
    vertices = [
      { x: 0, y: 0 },
      { x: base, y: 0 },
      { x: base + shear, y: height },
      { x: shear, y: height },
    ];
    formula = 'b·h';
  } else if (shape === 'triangle') {
    vertices = [
      { x: 0, y: 0 },
      { x: base, y: 0 },
      { x: shear, y: height },
    ];
    formula = '½·b·h';
  } else {
    // trapezoid
    const topBase = input.topBase ?? base * 0.6;
    const leftOffset = (base - topBase) / 2;
    vertices = [
      { x: 0, y: 0 },
      { x: base, y: 0 },
      { x: leftOffset + topBase + shear, y: height },
      { x: leftOffset + shear, y: height },
    ];
    formula = '½·(a+b)·h';
  }

  return { vertices, area: Math.abs(shoelaceArea(vertices)), formula };
}

/**
 * Select and return the appropriate PiecePlan for the given shape/config.
 */
export function selectDerivePlan(input: DerivePlanInput): PiecePlan {
  const { shape } = input;

  if (shape === 'parallelogram') {
    const base = input.base ?? 6;
    const height = input.height ?? 4;
    const shear = input.shear ?? 0;
    return parallelogramToRect(base, height, shear);
  }

  if (shape === 'triangle') {
    const tri = input.triangle ?? {
      a: { x: 0, y: 0 },
      b: { x: 6, y: 0 },
      c: { x: 2, y: 4 },
    };
    return triangleToParallelogram(tri);
  }

  if (shape === 'trapezoid') {
    const topBase = input.topBase ?? 3;
    const base = input.base ?? 5;
    const height = input.height ?? 4;
    return trapezoidToParallelogram(topBase, base, height);
  }

  if (shape === 'circle') {
    const r = input.radius ?? 4;
    const n = input.sectors ?? 16;
    return circleSectors(r, n);
  }

  if (shape === 'regular-polygon') {
    const n = (input as { polygonSides?: number }).polygonSides ?? 6;
    const r = (input as { circumradius?: number }).circumradius ?? 4;
    return apothemFan(n, r);
  }

  // fallback: parallelogram
  return parallelogramToRect(6, 4, 0);
}

/**
 * Compute the base length of the near-parallelogram formed by interleaving n sectors.
 * Base = n × r × sin(π/n), which approaches πr as n → ∞.
 */
export function circleBaseLength(r: number, n: number): number {
  return n * r * Math.sin(Math.PI / n);
}

/**
 * Build the regular polygon display model.
 * n-gon with circumradius r centred at origin.
 */
export function buildRegularPolygonModel(n: number, r: number): RegularPolygonModel {
  const vertices = regularPolygonVertices(n, r, { x: 0, y: 0 });
  const halfChord = r * Math.sin(Math.PI / n);
  const sideLength = 2 * halfChord;
  const perimeter = n * sideLength;
  const apothem = r * Math.cos(Math.PI / n);
  // Exact area = n × r² × sin(π/n) × cos(π/n) = ½ × perimeter × apothem
  const area = 0.5 * perimeter * apothem;
  return { vertices, perimeter, apothem, sideLength, area, n };
}

/** Default L-shape vertices (CCW, axis-aligned) for the composite demo. */
function lShapeVertices(): Vec2[] {
  // L-shape: 6×4 with top-right 3×2 removed
  // Outer box [0,0]→[6,0]→[6,4]→[0,4]
  // Inner notch cut: removes [3,2]→[6,2]→[6,4]→[3,4]
  return [
    { x: 0, y: 0 },
    { x: 6, y: 0 },
    { x: 6, y: 2 },
    { x: 3, y: 2 },
    { x: 3, y: 4 },
    { x: 0, y: 4 },
  ];
}

/**
 * Build the composite L-shape decomposition model.
 * Returns parts and their individual areas, plus the total area.
 */
export function buildCompositeModel(): CompositeModel {
  const figureVertices = lShapeVertices();
  const totalArea = Math.abs(shoelaceArea(figureVertices));
  const decomp = compositeDecompose(figureVertices);

  const parts: CompositePartModel[] = decomp.parts.map((poly) => ({
    vertices: poly,
    area: Math.abs(shoelaceArea(poly)),
  }));

  return {
    figureVertices,
    parts,
    totalArea,
    cuts: decomp.cuts,
  };
}

/**
 * Build the Heron cross-check model for a triangle given by 3 vertices.
 * Returns heronArea (from side lengths) and shoelaceArea (from coordinates).
 */
export function buildHeronModel(verts: [Vec2, Vec2, Vec2]): HeronModel {
  const [v0, v1, v2] = verts;
  const sideA = dist(v1, v2);
  const sideB = dist(v0, v2);
  const sideC = dist(v0, v1);
  const s = (sideA + sideB + sideC) / 2;
  const heronAreaVal = heron(sideA, sideB, sideC);
  const shoelaceAreaVal = Math.abs(shoelaceArea([v0, v1, v2]));

  return {
    heronArea: heronAreaVal,
    shoelaceArea: shoelaceAreaVal,
    sideA,
    sideB,
    sideC,
    semiPerimeter: s,
  };
}

// ============================================================================
// Constants
// ============================================================================

const STAGE_W = 800;
const STAGE_H = 460;
const GRID_UNIT = 40;

// Default shape state
const DEFAULT_SHAPE: AreaShape = 'parallelogram';
const DEFAULT_BASE = 6;
const DEFAULT_HEIGHT = 4;
const DEFAULT_SHEAR = 1;
const DEFAULT_TOP_BASE = 3;
const DEFAULT_RADIUS = 4;
const DEFAULT_SECTORS = 16;
const DEFAULT_POLY_SIDES = 6;
const DEFAULT_POLY_RADIUS = 4;

// Heron draggable triangle default positions (screen coords)
const DEFAULT_HERON_VERTS: [Vec2, Vec2, Vec2] = [
  { x: 220, y: 340 },
  { x: 580, y: 340 },
  { x: 360, y: 140 },
];

// ============================================================================
// State shape
// ============================================================================

interface AreaState {
  shape: AreaShape;
  base: number;
  height: number;
  shear: number;
  topBase: number;
  circleRadius: number;
  circleSectorCount: number;
  polyN: number;
  polyR: number;
  showGrid: boolean;
  scrubT: number;
  heronVerts: [Vec2, Vec2, Vec2];
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): ExperimentSpec<AreaState, GeometryKey, GeometryContext> {
  // Closure-stashed handles created in setup()
  let grid: GridHandle;
  let figure: FigureHandle;
  let readout: ReadoutHandle;
  let controllerRef: HarnessController<AreaState>;
  let dissection: DissectionHandle | null = null;
  let lastPlanKey: string | null = null;

  // fmt helper
  function fmt(n: number, decimals = 2): string {
    const rounded = Math.round(n * Math.pow(10, decimals)) / Math.pow(10, decimals);
    if (Number.isInteger(rounded)) return String(rounded);
    return rounded.toFixed(decimals);
  }

  function defaultState(): AreaState {
    return {
      shape: DEFAULT_SHAPE,
      base: DEFAULT_BASE,
      height: DEFAULT_HEIGHT,
      shear: DEFAULT_SHEAR,
      topBase: DEFAULT_TOP_BASE,
      circleRadius: DEFAULT_RADIUS,
      circleSectorCount: DEFAULT_SECTORS,
      polyN: DEFAULT_POLY_SIDES,
      polyR: DEFAULT_POLY_RADIUS,
      showGrid: true,
      scrubT: 0,
      heronVerts: [
        { ...DEFAULT_HERON_VERTS[0] },
        { ...DEFAULT_HERON_VERTS[1] },
        { ...DEFAULT_HERON_VERTS[2] },
      ],
    };
  }

  function effectiveModeFor(state: AreaState, advanced: boolean): AreaShape {
    const advancedModes: AreaShape[] = ['regular-polygon', 'composite', 'heron'];
    return !advanced && advancedModes.includes(state.shape) ? 'parallelogram' : state.shape;
  }

  function applyPresetLocal(state: AreaState, presetId: string, advanced: boolean): AreaState {
    const preset = AREA_PRESETS.find((p) => p.id === presetId);
    if (!preset || (preset.advancedOnly && !advanced)) return state;

    const next: AreaState = { ...state, shape: preset.shape, scrubT: 0 };
    if (preset.base !== undefined) next.base = preset.base;
    if (preset.height !== undefined) next.height = preset.height;
    if (preset.shear !== undefined) next.shear = preset.shear;
    if (preset.topBase !== undefined) next.topBase = preset.topBase;
    if (preset.radius !== undefined) next.circleRadius = preset.radius;
    if (preset.sectors !== undefined) next.circleSectorCount = preset.sectors;
    if (preset.polygonSides !== undefined) next.polyN = preset.polygonSides;
    if (preset.circumradius !== undefined) next.polyR = preset.circumradius;
    return next;
  }

  function syncDissection(
    state: AreaState,
    planKey: string,
    plan: PiecePlan,
    cx: number,
    cy: number,
    scale: number,
    flipY: boolean,
    shearXPerY: number,
  ): void {
    if (planKey !== lastPlanKey) {
      if (dissection) {
        dissection.destroy();
        dissection = null;
      }
      dissection = controllerRef.createDissection({ plan, width: STAGE_W, height: STAGE_H });
      const sy = flipY ? -scale : scale;
      const shearMatrix = shearXPerY !== 0 ? ` matrix(1 0 ${shearXPerY} 1 0 0)` : '';
      dissection.group.setAttribute(
        'transform',
        `translate(${cx} ${cy}) scale(${scale} ${sy})${shearMatrix}`,
      );
      lastPlanKey = planKey;
    }
    if (dissection) {
      dissection.setT(state.scrubT);
    }
  }

  function clearDissection(): void {
    if (dissection) {
      dissection.destroy();
      dissection = null;
    }
    lastPlanKey = null;
  }

  return {
    labelKey: 'experiment.area-lab',
    demoId: '2d-geometry',
    advancedKey: 'area-lab',

    initialState(): AreaState {
      return defaultState();
    },

    applyPreset(state, presetId, ctx): AreaState {
      return applyPresetLocal(state, presetId, ctx.advanced);
    },

    setup(_ctx, controller) {
      controllerRef = controller;
      grid = controller.createGrid({
        width: STAGE_W,
        height: STAGE_H,
        unit: GRID_UNIT,
        faint: true,
      });
      figure = controller.createFigure();
      readout = controller.createReadout();

      controller.registerCleanup(() => {
        if (dissection) {
          dissection.destroy();
          dissection = null;
        }
        lastPlanKey = null;
      });
    },

    render(state: AreaState, ctx): void {
      const effectiveMode = effectiveModeFor(state, ctx.advanced);

      // Grid visibility
      const gridGroup = (grid as unknown as { group?: SVGElement }).group;
      if (state.showGrid) {
        gridGroup?.removeAttribute('display');
      } else {
        gridGroup?.setAttribute('display', 'none');
      }

      switch (effectiveMode) {
        case 'parallelogram':
          renderParallelogram(state);
          break;
        case 'triangle':
          renderTriangle(state);
          break;
        case 'trapezoid':
          renderTrapezoid(state);
          break;
        case 'circle':
          renderCircle(state);
          break;
        case 'regular-polygon':
          renderRegularPolygon(state);
          break;
        case 'composite':
          renderComposite(state);
          break;
        case 'heron':
          renderHeron(state);
          break;
      }

      function renderParallelogram(state: AreaState): void {
        const model = buildShearModel({
          shape: 'parallelogram',
          base: state.base,
          height: state.height,
          shear: state.shear,
        });
        const cx = STAGE_W / 2 - (state.base / 2) * GRID_UNIT;
        const cy = STAGE_H / 2 - (state.height / 2) * GRID_UNIT;

        const pts = model.vertices.map((v) => ({
          x: cx + v.x * GRID_UNIT,
          y: cy + (state.height - v.y) * GRID_UNIT,
        }));

        const centroid = {
          x: (pts[0]!.x + pts[1]!.x + pts[2]!.x + pts[3]!.x) / 4,
          y: (pts[0]!.y + pts[1]!.y + pts[2]!.y + pts[3]!.y) / 4,
        };

        const elems: FigureElement[] = [
          { kind: 'point', id: 'pl-0', at: pts[0]! },
          { kind: 'point', id: 'pl-1', at: pts[1]! },
          { kind: 'point', id: 'pl-2', at: pts[2]! },
          { kind: 'point', id: 'pl-3', at: pts[3]! },
          {
            kind: 'polygon',
            id: 'poly-shape',
            vertices: ['pl-0', 'pl-1', 'pl-2', 'pl-3'],
            style: { className: 'geo-square-a' },
          },
          { kind: 'point', id: 'area-center', at: centroid },
          {
            kind: 'label',
            id: 'area-value',
            anchor: 'area-center',
            offset: { x: 0, y: 0 },
            text: `A = ${fmt(model.area)}`,
          },
        ];
        figure.update(elems);

        const plan = selectDerivePlan({
          shape: 'parallelogram',
          base: state.base,
          height: state.height,
          shear: state.shear,
        });
        const planKey = `para:${state.base},${state.height},${state.shear}`;
        syncDissection(state, planKey, plan, cx, cy + state.height * GRID_UNIT, GRID_UNIT, true, 0);

        const rows: ReadoutRow[] = [
          {
            id: 'area-formula',
            lhs: 'A = b·h',
            relation: '=',
            rhs: `${fmt(state.base)}·${fmt(state.height)} = ${fmt(model.area)}`,
            emphasis: 'invariant',
          },
          {
            id: 'shear-note',
            lhs: 'shear',
            relation: '=',
            rhs: fmt(state.shear),
          },
        ];
        readout.update(rows);
      }

      function renderTriangle(state: AreaState): void {
        const cx = STAGE_W / 2 - (state.base / 2) * GRID_UNIT;
        const cy = STAGE_H / 2 - (state.height / 2) * GRID_UNIT;

        const tri: Triangle = {
          a: { x: cx, y: cy + state.height * GRID_UNIT },
          b: { x: cx + state.base * GRID_UNIT, y: cy + state.height * GRID_UNIT },
          c: { x: cx + state.shear * GRID_UNIT, y: cy },
        };

        const centroid = {
          x: (tri.a.x + tri.b.x + tri.c.x) / 3,
          y: (tri.a.y + tri.b.y + tri.c.y) / 3,
        };

        const area = 0.5 * state.base * state.height;

        const elems: FigureElement[] = [
          { kind: 'point', id: 'tri-0', at: tri.a },
          { kind: 'point', id: 'tri-1', at: tri.b },
          { kind: 'point', id: 'tri-2', at: tri.c },
          {
            kind: 'polygon',
            id: 'poly-shape',
            vertices: ['tri-0', 'tri-1', 'tri-2'],
            style: { className: 'geo-square-b' },
          },
          { kind: 'point', id: 'area-center', at: centroid },
          {
            kind: 'label',
            id: 'area-value',
            anchor: 'area-center',
            offset: { x: 0, y: 0 },
            text: `A = ${fmt(area)}`,
          },
        ];
        figure.update(elems);

        const geoTri: Triangle = {
          a: { x: 0, y: 0 },
          b: { x: state.base, y: 0 },
          c: { x: state.shear, y: state.height },
        };
        const plan = triangleToParallelogram(geoTri);
        const planKey = `tri:${state.base},${state.height},${state.shear}`;
        syncDissection(state, planKey, plan, cx, cy + state.height * GRID_UNIT, GRID_UNIT, true, 0);

        const rows: ReadoutRow[] = [
          {
            id: 'area-formula',
            lhs: 'A = ½·b·h',
            relation: '=',
            rhs: `½·${fmt(state.base)}·${fmt(state.height)} = ${fmt(area)}`,
            emphasis: 'invariant',
          },
        ];
        readout.update(rows);
      }

      function renderTrapezoid(state: AreaState): void {
        const model = buildShearModel({
          shape: 'trapezoid',
          base: state.base,
          topBase: state.topBase,
          height: state.height,
          shear: state.shear,
        });
        const cx = STAGE_W / 2 - (state.base / 2) * GRID_UNIT;
        const cy = STAGE_H / 2 - (state.height / 2) * GRID_UNIT;

        const pts = model.vertices.map((v) => ({
          x: cx + v.x * GRID_UNIT,
          y: cy + (state.height - v.y) * GRID_UNIT,
        }));

        const centroid = {
          x: (pts[0]!.x + pts[1]!.x + pts[2]!.x + pts[3]!.x) / 4,
          y: (pts[0]!.y + pts[1]!.y + pts[2]!.y + pts[3]!.y) / 4,
        };

        const elems: FigureElement[] = [
          { kind: 'point', id: 'trap-0', at: pts[0]! },
          { kind: 'point', id: 'trap-1', at: pts[1]! },
          { kind: 'point', id: 'trap-2', at: pts[2]! },
          { kind: 'point', id: 'trap-3', at: pts[3]! },
          {
            kind: 'polygon',
            id: 'poly-shape',
            vertices: ['trap-0', 'trap-1', 'trap-2', 'trap-3'],
            style: { className: 'geo-square-a' },
          },
          { kind: 'point', id: 'area-center', at: centroid },
          {
            kind: 'label',
            id: 'area-value',
            anchor: 'area-center',
            offset: { x: 0, y: 0 },
            text: `A = ${fmt(model.area)}`,
          },
        ];
        figure.update(elems);

        const plan = trapezoidToParallelogram(state.topBase, state.base, state.height);
        const planKey = `trap:${state.topBase},${state.base},${state.height},${state.shear}`;
        syncDissection(
          state,
          planKey,
          plan,
          cx,
          cy + state.height * GRID_UNIT,
          GRID_UNIT,
          true,
          state.shear / state.height,
        );

        const rows: ReadoutRow[] = [
          {
            id: 'area-formula',
            lhs: 'A = ½·(a+b)·h',
            relation: '=',
            rhs: `½·(${fmt(state.topBase)}+${fmt(state.base)})·${fmt(state.height)} = ${fmt(model.area)}`,
            emphasis: 'invariant',
          },
          {
            id: 'shear-note',
            lhs: 'shear',
            relation: '=',
            rhs: fmt(state.shear),
          },
        ];
        readout.update(rows);
      }

      function renderCircle(state: AreaState): void {
        const r = state.circleRadius;
        const n = state.circleSectorCount;
        const cx = STAGE_W / 2;
        const cy = STAGE_H / 2;

        const arcN = 64;
        const elems: FigureElement[] = [];
        for (let i = 0; i < arcN; i++) {
          const angle = (i / arcN) * 2 * Math.PI;
          elems.push({
            kind: 'point',
            id: `circ-${i}`,
            at: {
              x: cx + r * GRID_UNIT * Math.cos(angle),
              y: cy + r * GRID_UNIT * Math.sin(angle),
            },
          });
        }
        elems.push({
          kind: 'polygon',
          id: 'poly-shape',
          vertices: elems.slice(0, arcN).map((_, i) => `circ-${i}`),
          style: { className: 'geo-square-a' },
        });

        const area = Math.PI * r * r;
        elems.push({ kind: 'point', id: 'area-center', at: { x: cx, y: cy } });
        elems.push({
          kind: 'label',
          id: 'area-value',
          anchor: 'area-center',
          offset: { x: 0, y: 0 },
          text: `A = ${fmt(area, 3)}`,
        });

        figure.update(elems);

        const plan = circleSectors(r, n);
        const planKey = `circ:${r},${n}`;
        syncDissection(state, planKey, plan, cx, cy, GRID_UNIT, false, 0);

        const baseLen = circleBaseLength(r, n);
        const rows: ReadoutRow[] = [
          {
            id: 'area-formula',
            lhs: 'A = πr²',
            relation: '=',
            rhs: `π·${fmt(r)}² ≈ ${fmt(area, 3)}`,
            emphasis: 'invariant',
          },
          {
            id: 'base-len',
            lhs: `base (n=${n})`,
            relation: '≈',
            rhs: `${fmt(baseLen, 3)} → πr=${fmt(Math.PI * r, 3)}`,
          },
        ];
        readout.update(rows);
      }

      function renderRegularPolygon(state: AreaState): void {
        const model = buildRegularPolygonModel(state.polyN, state.polyR);
        const cx = STAGE_W / 2;
        const cy = STAGE_H / 2;

        const elems: FigureElement[] = model.vertices.map((v, i) => ({
          kind: 'point' as const,
          id: `poly-v-${i}`,
          at: { x: cx + v.x * GRID_UNIT, y: cy + v.y * GRID_UNIT },
        }));
        elems.push({
          kind: 'polygon',
          id: 'poly-shape',
          vertices: model.vertices.map((_, i) => `poly-v-${i}`),
          style: { className: 'geo-square-a' },
        });

        elems.push({ kind: 'point', id: 'area-center', at: { x: cx, y: cy } });
        elems.push({
          kind: 'label',
          id: 'area-value',
          anchor: 'area-center',
          offset: { x: 0, y: 0 },
          text: `A = ${fmt(model.area, 3)}`,
        });

        figure.update(elems);

        const plan = apothemFan(state.polyN, state.polyR);
        const planKey = `poly:${state.polyN},${state.polyR}`;
        syncDissection(state, planKey, plan, cx, cy, GRID_UNIT, false, 0);

        const halfPA = 0.5 * model.perimeter * model.apothem;
        const rows: ReadoutRow[] = [
          {
            id: 'area-formula',
            lhs: 'A = ½·P·a',
            relation: '=',
            rhs: `½·${fmt(model.perimeter, 2)}·${fmt(model.apothem, 2)} = ${fmt(halfPA, 3)}`,
            emphasis: 'invariant',
          },
          {
            id: 'n-gon',
            lhs: `n=${state.polyN}`,
            relation: '=',
            rhs: `side=${fmt(model.sideLength, 2)}, apo=${fmt(model.apothem, 2)}`,
          },
        ];
        readout.update(rows);
      }

      function renderComposite(_state: AreaState): void {
        const model = buildCompositeModel();
        const cx = STAGE_W / 2 - 3 * GRID_UNIT;
        const cy = STAGE_H / 2 - 2 * GRID_UNIT;

        const figPts = model.figureVertices.map((v) => ({
          x: cx + v.x * GRID_UNIT,
          y: cy + v.y * GRID_UNIT,
        }));

        let signedArea = 0;
        let cxAcc = 0;
        let cyAcc = 0;
        for (let i = 0; i < figPts.length; i++) {
          const a = figPts[i]!;
          const b = figPts[(i + 1) % figPts.length]!;
          const crossTerm = a.x * b.y - b.x * a.y;
          signedArea += crossTerm;
          cxAcc += (a.x + b.x) * crossTerm;
          cyAcc += (a.y + b.y) * crossTerm;
        }
        signedArea *= 0.5;
        const centroid =
          Math.abs(signedArea) < 1e-9
            ? { x: figPts[0]!.x, y: figPts[0]!.y }
            : { x: cxAcc / (6 * signedArea), y: cyAcc / (6 * signedArea) };

        const elems: FigureElement[] = figPts.map((p, i) => ({
          kind: 'point' as const,
          id: `fig-${i}`,
          at: p,
        }));
        elems.push({
          kind: 'polygon',
          id: 'poly-shape',
          vertices: figPts.map((_, i) => `fig-${i}`),
          style: { className: 'geo-square-a' },
        });

        model.cuts.forEach((cut, i) => {
          const from = { x: cx + cut[0].x * GRID_UNIT, y: cy + cut[0].y * GRID_UNIT };
          const to = { x: cx + cut[1].x * GRID_UNIT, y: cy + cut[1].y * GRID_UNIT };
          const fromId = `cut-from-${i}`;
          const toId = `cut-to-${i}`;
          elems.push({ kind: 'point', id: fromId, at: from });
          elems.push({ kind: 'point', id: toId, at: to });
          elems.push({
            kind: 'segment',
            id: `cut-${i}`,
            from: fromId,
            to: toId,
            style: { className: 'geo-tri-hyp', dashed: true },
          });
        });

        elems.push({ kind: 'point', id: 'area-center', at: centroid });
        elems.push({
          kind: 'label',
          id: 'area-value',
          anchor: 'area-center',
          offset: { x: 0, y: 0 },
          text: `A = ${fmt(model.totalArea)}`,
        });

        figure.update(elems);
        clearDissection();

        const partSum = model.parts.reduce((s, p) => s + p.area, 0);
        const cleanRows: ReadoutRow[] = model.parts.map((p, i) => ({
          id: `part-${i}`,
          lhs: `Part ${i + 1}`,
          relation: '=',
          rhs: fmt(p.area),
        }));
        cleanRows.push({
          id: 'total',
          lhs: `${model.parts.map((p) => fmt(p.area)).join('+')}`,
          relation: '=',
          rhs: fmt(partSum),
          emphasis: 'invariant',
        });
        readout.update(cleanRows);
      }

      function renderHeron(state: AreaState): void {
        const model = buildHeronModel(state.heronVerts);
        const [v0, v1, v2] = state.heronVerts;

        const centroid = {
          x: (v0.x + v1.x + v2.x) / 3,
          y: (v0.y + v1.y + v2.y) / 3,
        };

        const elems: FigureElement[] = [
          { kind: 'point', id: 'heron-0', at: v0, draggable: {} },
          { kind: 'point', id: 'heron-1', at: v1, draggable: {} },
          { kind: 'point', id: 'heron-2', at: v2, draggable: {} },
          {
            kind: 'polygon',
            id: 'poly-shape',
            vertices: ['heron-0', 'heron-1', 'heron-2'],
            style: { className: 'geo-square-b' },
          },
          { kind: 'label', id: 'lbl-A', anchor: 'heron-0', offset: { x: -16, y: 10 }, text: 'A' },
          { kind: 'label', id: 'lbl-B', anchor: 'heron-1', offset: { x: 8, y: 10 }, text: 'B' },
          { kind: 'label', id: 'lbl-C', anchor: 'heron-2', offset: { x: 8, y: -8 }, text: 'C' },
          { kind: 'point', id: 'area-center', at: centroid },
          {
            kind: 'label',
            id: 'area-value',
            anchor: 'area-center',
            offset: { x: 0, y: 0 },
            text: `A = ${fmt(model.heronArea, 4)}`,
          },
        ];
        figure.update(elems);
        clearDissection();

        const rows: ReadoutRow[] = [
          {
            id: 'heron-sides',
            lhs: 'a,b,c',
            relation: '=',
            rhs: `${fmt(model.sideA)}, ${fmt(model.sideB)}, ${fmt(model.sideC)}`,
          },
          {
            id: 'heron-formula',
            lhs: '√(s(s-a)(s-b)(s-c))',
            relation: '=',
            rhs: fmt(model.heronArea, 4),
            emphasis: 'invariant',
          },
          {
            id: 'shoelace-check',
            lhs: 'shoelace area',
            relation: '=',
            rhs: fmt(model.shoelaceArea, 4),
            emphasis:
              Math.abs(model.heronArea - model.shoelaceArea) < 1e-6 ? 'invariant' : 'broken',
          },
        ];
        readout.update(rows);
      }
    },

    panel(state: AreaState, ctx): PanelRow<GeometryKey, AreaState>[] {
      const effectiveMode = effectiveModeFor(state, ctx.advanced);
      const isCircle = effectiveMode === 'circle';
      const isPoly = effectiveMode === 'regular-polygon';
      const isComposite = effectiveMode === 'composite';
      const isHeron = effectiveMode === 'heron';
      const hasShear = !isCircle && !isPoly && !isComposite && !isHeron;

      const rows: PanelRow<GeometryKey, AreaState>[] = [
        {
          kind: 'dropdown',
          id: 'area-shape',
          labelKey: 'area.panel.shape',
          options: (() => {
            const opts: { value: string; labelKey: GeometryKey }[] = [
              { value: 'parallelogram', labelKey: 'area.shape.parallelogram' },
              { value: 'triangle', labelKey: 'area.shape.triangle' },
              { value: 'trapezoid', labelKey: 'area.shape.trapezoid' },
              { value: 'circle', labelKey: 'area.shape.circle' },
            ];
            if (ctx.advanced) {
              opts.push(
                { value: 'regular-polygon', labelKey: 'area.shape.regular-polygon' },
                { value: 'composite', labelKey: 'area.shape.composite' },
                { value: 'heron', labelKey: 'area.shape.heron' },
              );
            }
            return opts;
          })(),
          value: effectiveMode,
          onChange: (v) => ({ ...state, shape: v as AreaShape, scrubT: 0 }),
        },
      ];

      if (hasShear) {
        rows.push({
          kind: 'slider',
          id: 'area-shear',
          labelKey: 'area.panel.shear',
          min: 0,
          max: 4,
          step: 0.1,
          value: state.shear,
          format: (v) => fmt(v),
          onChange: (v) => ({ ...state, shear: v }),
        });
      }

      if (isCircle) {
        rows.push({
          kind: 'slider',
          id: 'area-sectors',
          labelKey: 'area.panel.sectors',
          min: 4,
          max: 64,
          step: 2,
          value: state.circleSectorCount,
          format: (v) => String(v),
          onChange: (v) => ({ ...state, circleSectorCount: v, scrubT: 0 }),
        });
      }

      if (isPoly) {
        rows.push({
          kind: 'slider',
          id: 'area-polygon-n',
          labelKey: 'area.panel.polygon-n',
          min: 3,
          max: 16,
          step: 1,
          value: state.polyN,
          format: (v) => String(v),
          onChange: (v) => ({ ...state, polyN: v, scrubT: 0 }),
        });
      }

      if (!isComposite && !isHeron) {
        rows.push({
          kind: 'slider',
          id: 'area-scrubber',
          labelKey: 'area.panel.scrubber',
          min: 0,
          max: 100,
          step: 1,
          value: Math.round(state.scrubT * 100),
          format: (v) => `${v}%`,
          onChange: (v) => ({ ...state, scrubT: v / 100 }),
        });
      }

      rows.push({
        kind: 'toggle',
        labelKey: 'area.panel.show-grid',
        value: state.showGrid,
        onChange: (v) => ({ ...state, showGrid: v }),
      });

      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions: (_state: AreaState, ctx): ActionBarSpec<GeometryKey, AreaState> => ({
      onReset: () => defaultState(),
      onRandomise: (s) => {
        const effMode = effectiveModeFor(s, ctx.advanced);
        const next = { ...s, scrubT: 0 };
        if (effMode === 'circle') {
          let c = 4 + Math.floor(ctx.prng() * 30) * 2;
          c = Math.max(4, Math.min(64, c));
          next.circleSectorCount = c;
        } else if (effMode === 'regular-polygon') {
          next.polyN = 3 + Math.floor(ctx.prng() * 10);
        } else {
          next.shear = Math.round(ctx.prng() * 40) / 10;
        }
        return next;
      },
      presets: AREA_PRESETS.filter((p) => ctx.advanced || !p.advancedOnly).map((p) => ({
        id: p.id,
        labelKey: p.labelKey,
      })),
      onPreset: (s, id) => applyPresetLocal(s, id, ctx.advanced),
    }),

    onDrag(state: AreaState, pointId: string, at: Vec2): AreaState {
      if (state.shape === 'heron') {
        if (pointId === 'heron-0')
          return { ...state, heronVerts: [at, state.heronVerts[1], state.heronVerts[2]] };
        if (pointId === 'heron-1')
          return { ...state, heronVerts: [state.heronVerts[0], at, state.heronVerts[2]] };
        if (pointId === 'heron-2')
          return { ...state, heronVerts: [state.heronVerts[0], state.heronVerts[1], at] };
      }
      return state;
    },

    applyShotState(state: AreaState, _shotId: string): AreaState {
      const posed = applyPresetLocal(state, 'parallelogram-3x4', true);
      return { ...posed, scrubT: 0.5 };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
